import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import Icon from "../../UI/Icon";
import ProductImage from "../../UI/ProductImage";
import ProductGrid from "../../UI/ProductGrid";
import CategoryCard from "../../UI/CategoryCard";
import { Button, LoadError, SectionHeader, isPrerender } from "../../UI/Primitives";
import SearchBar from "../../Layout/SearchBar";
import useInView from "../../../hooks/useInView";
import * as endpoint from "../../../Service/endpoints";
import { HERO_IMAGE, NAV_CATEGORIES, getCategoryMeta } from "../Catalog/categoryMeta";

/* ------------------------------ Hero ------------------------------ */

export function HomeHero({ cityName }) {
  const dispatch = useDispatch();
  return (
    <section className="v-hero" aria-labelledby="v-hero-title">
      <div className="v-container v-hero__inner">
        <div className="v-hero__copy">
          <span className="v-eyebrow">Furniture &amp; appliances on rent · {cityName}</span>
          <h1 id="v-hero-title" className="v-hero__title">
            Live better.
            <span className="v-hero__accent">Rent smarter.</span>
          </h1>
          <p className="v-hero__lead">
            Quality-checked furniture and appliances on flexible monthly plans — delivered, installed and maintained
            by Voorent.
          </p>

          <div className="v-hero__search">
            <SearchBar variant="hero" placeholder="Try “sofa”, “bed” or “fridge”" />
          </div>

          <div className="v-hero__actions">
            <Button to={endpoint.getPath("furniture")} size="lg" arrow>
              Explore furniture
            </Button>
            <Button to={endpoint.getPath("package")} variant="secondary" size="lg">
              View packages
            </Button>
            <button type="button" className="v-hero__city" onClick={() => dispatch({ type: "user.city.show" })}>
              <Icon name="pin" size={16} />
              Delivering in <strong>{cityName}</strong>
              <span className="v-hero__city-change">Change</span>
            </button>
          </div>

          <div className="v-hero__popular">
            <span className="v-hero__popular-label">Popular</span>
            <ul className="v-chip-row">
              {NAV_CATEGORIES.map((slug) => (
                <li key={slug}>
                  <Link className="v-chip" to={endpoint.getPath(slug)}>
                    {getCategoryMeta(slug).short}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="v-hero__visual">
          <div className="v-hero__frame">
            <ProductImage
              src={HERO_IMAGE}
              alt="Premium sectional sofa from the Voorent premium collection in a styled living room"
              fit="cover"
              ratio="4 / 5"
              width={1500}
              height={1000}
              priority
              sizes="(min-width: 1024px) 44vw, 100vw"
            />
          </div>
          <Link to={endpoint.getPath("premium")} className="v-hero__float v-hero__float--bottom">
            <span className="v-hero__float-icon">
              <Icon name="sparkle" size={18} />
            </span>
            <span>
              <strong>Premium collection</strong>
              <span>Designer pieces on 12-month plans</span>
            </span>
            <Icon name="arrowRight" size={16} className="v-btn__arrow" />
          </Link>
          <div className="v-hero__float v-hero__float--top" aria-hidden="true">
            <span className="v-hero__pulse" />
            Quality checked before dispatch
          </div>
        </div>
      </div>
    </section>
  );
}

/* --------------------------- Trust strip --------------------------- */

const PROMISES = [
  { icon: "shield", title: "Quality rentals", text: "At the best prices" },
  { icon: "truck", title: "Free delivery & maintenance", text: "Delivered, installed and serviced" },
  { icon: "calendar", title: "Flexible rental plans", text: "Choose the tenure that fits your stay" },
  { icon: "clock", title: "24/7 customer support", text: "Customisation and easy returns" },
];

export function TrustStrip() {
  return (
    <section className="v-container" aria-label="The Voorent promise">
      <ul className="v-promises">
        {PROMISES.map((item) => (
          <li key={item.title} className="v-promise">
            <span className="v-promise__icon">
              <Icon name={item.icon} size={20} />
            </span>
            <span>
              <strong>{item.title}</strong>
              <span>{item.text}</span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* --------------------------- Categories --------------------------- */

export function CategoryShowcase({ city }) {
  const slugs = [...NAV_CATEGORIES];
  if (city === "gurgaon") slugs.push("expressDelivery");
  return (
    <section className="v-section v-container" aria-labelledby="v-cats-title">
      <SectionHeader
        id="v-cats-title"
        eyebrow="Shop by category"
        title="Everything for a ready home"
        description="From a single appliance to a fully furnished flat — rented monthly."
      />
      <div className={`v-bento v-bento--${slugs.length}`}>
        {slugs.map((slug, index) => {
          const meta = getCategoryMeta(slug);
          return (
            <CategoryCard
              key={slug}
              to={endpoint.getPath(slug)}
              title={meta.short}
              description={meta.description}
              image={meta.image}
              size={index === 0 ? "lg" : "md"}
            />
          );
        })}
      </div>
    </section>
  );
}

/* ------------------------ Product sections ------------------------ */

/**
 * A homepage product rail backed by a real API call. With `defer`, the request
 * is only made when the section approaches the viewport. Sections with no
 * products hide themselves instead of showing invented content.
 */
export function ProductSection({ id, eyebrow, title, description, load, limit = 4, viewAll, defer = false, tone }) {
  const [ref, inView] = useInView("600px");
  const [state, setState] = useState({ status: "idle", items: [] });
  const shouldLoad = !defer || inView;

  const fetchItems = useCallback(() => {
    setState((prev) => ({ ...prev, status: "loading" }));
    load().then(
      (items) => setState({ status: "done", items: items.slice(0, limit) }),
      () => setState({ status: "error", items: [] })
    );
  }, [load, limit]);

  useEffect(() => {
    if (shouldLoad && state.status === "idle") fetchItems();
  }, [shouldLoad, state.status, fetchItems]);

  if (state.status === "done" && state.items.length === 0) return null;

  return (
    <section ref={ref} className={`v-section ${tone ? `v-band v-band--${tone}` : ""}`} aria-labelledby={id}>
      <div className="v-container">
        <SectionHeader
          id={id}
          eyebrow={eyebrow}
          title={title}
          description={description}
          action={
            viewAll ? (
              <Link to={viewAll} className="v-link">
                View all <Icon name="arrowRight" size={16} className="v-btn__arrow" />
              </Link>
            ) : null
          }
        />
        {state.status === "error" && !isPrerender() ? (
          <LoadError compact onRetry={fetchItems} />
        ) : (
          <ProductGrid
            variant="rail"
            label={title}
            loading={state.status !== "done"}
            skeletonCount={4}
            products={state.items}
          />
        )}
      </div>
    </section>
  );
}

/* --------------------------- How it works --------------------------- */

const STEPS = [
  { icon: "search", title: "Pick your setup", text: "Browse by room, category or monthly budget." },
  { icon: "calendar", title: "Choose tenure", text: "Select the plan that matches your stay." },
  { icon: "truck", title: "We deliver", text: "Voorent handles delivery, setup and service." },
];

export function HowItWorks() {
  return (
    <section className="v-section v-container" aria-labelledby="v-how-title">
      <SectionHeader id="v-how-title" eyebrow="How Voorent works" title="Browse to living-room ready in three steps" />
      <ol className="v-steps">
        {STEPS.map((step, index) => (
          <li key={step.title} className="v-step">
            <span className="v-step__num">{String(index + 1).padStart(2, "0")}</span>
            <span className="v-step__icon">
              <Icon name={step.icon} size={22} />
            </span>
            <h3 className="v-step__title">{step.title}</h3>
            <p className="v-step__text">{step.text}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

/* ---------------------------- Why Voorent ---------------------------- */

const REASONS = [
  {
    icon: "wrench",
    title: "No maintenance",
    text: "Whether you rent furniture or an appliance, you won’t pay extra for maintenance — we keep your things up and working.",
  },
  {
    icon: "wallet",
    title: "Zero loan responsibilities",
    text: "Set up your home without a loan account or instalments to clear. Renting keeps your finances free.",
  },
  {
    icon: "tag",
    title: "No budget limitations",
    text: "Keep big purchases aside and rent the sofa you actually want, on a monthly plan.",
  },
  {
    icon: "shield",
    title: "Impeccable support",
    text: "Face an issue after delivery? Our experienced customer team works for the earliest resolution.",
  },
];

export function WhyVoorent() {
  return (
    <section className="v-section v-container" aria-labelledby="v-why-title">
      <div className="v-why">
        <div className="v-why__intro">
          <span className="v-eyebrow">Why Voorent</span>
          <h2 id="v-why-title" className="v-section-header__title">
            The ease of owning, without the weight of it
          </h2>
          <p className="v-section-header__desc">
            Renting from Voorent means verified products, clear monthly pricing and one team that owns delivery, set-up
            and service.
          </p>
          <Button to={endpoint.getPath("furniture")} variant="tonal" arrow>
            Start browsing
          </Button>
        </div>
        <ul className="v-why__grid">
          {REASONS.map((reason) => (
            <li key={reason.title} className="v-why__item">
              <span className="v-why__icon">
                <Icon name={reason.icon} size={20} />
              </span>
              <h3>{reason.title}</h3>
              <p>{reason.text}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ------------------------------ Reviews ------------------------------ */

const STORAGE = "https://voorent.com/voorent-storage/images";

const CUSTOMER_REVIEWS = [
  {
    text: "Great experience. Great service. The quality of furniture is very good and the rental rates are very affordable. Strongly recommended.",
    name: "Rajat",
    meta: "Gurgaon",
    image: `${STORAGE}/rajat.png`,
  },
  {
    text: "Just what I wanted. Verification is easy and hassle free. Delivery on time and quality of furniture is awesome. Keep it up like that.",
    name: "Shraddha",
    meta: "Delhi",
    image: `${STORAGE}/shraddha.png`,
  },
  {
    text: "Their service is excellent and so is the quality of the furniture that Voorent provides. The delivery boys seated the furniture as desired.",
    name: "Tanuj",
    meta: "Noida",
    image: `${STORAGE}/tanuj.png`,
  },
];

const BUSINESS_REVIEWS = [
  {
    text: "Voorent has been our associate for more than 3 years and have always delivered high quality furniture backed by prompt services. … Fast, reliable and cost effective.",
    name: "Amber Sajid",
    meta: "Co-Founder, COHO",
  },
  {
    text: "My experience with the Voorent team has been the best till date. They were the first company to deliver exactly what was promised, and exactly on time. The quality of the furniture was also worth the price.",
    name: "Sunny Garg",
    meta: "Founder/CEO, YourShell",
  },
  {
    text: "We have recently started our partnership with Voorent, and the experience has been good. They were flexible and listened to our suggestions.",
    name: "Shubha Lal",
    meta: "Co-Founder, Yourspace",
  },
  {
    text: "Voorent has become a very reliable partner who, with their specialization in providing customized furniture, are giving a unique look to our properties. They are very efficient in their services and always provide high quality furniture.",
    name: "Mohammed Sirajuddin",
    meta: "Co-Founder, Creator Gurukul",
  },
];

function ReviewCard({ review }) {
  return (
    <figure className="v-review">
      <blockquote className="v-review__text">“{review.text}”</blockquote>
      <figcaption className="v-review__who">
        {review.image ? (
          <img src={review.image} alt="" width="40" height="40" loading="lazy" decoding="async" />
        ) : (
          <span className="v-review__initial" aria-hidden="true">
            {review.name.charAt(0)}
          </span>
        )}
        <span>
          <strong>{review.name}</strong>
          <span>{review.meta}</span>
        </span>
      </figcaption>
    </figure>
  );
}

export function Reviews() {
  return (
    <section className="v-section v-container" aria-labelledby="v-reviews-title">
      <SectionHeader
        id="v-reviews-title"
        eyebrow="Customer reviews"
        title="Homes and businesses that rent with us"
      />
      <div className="v-reviews">
        {CUSTOMER_REVIEWS.map((review) => (
          <ReviewCard key={review.name} review={review} />
        ))}
      </div>
      <h3 className="v-reviews__sub">What our corporate customers say</h3>
      <div className="v-reviews v-reviews--b2b">
        {BUSINESS_REVIEWS.map((review) => (
          <ReviewCard key={review.name} review={review} />
        ))}
      </div>
    </section>
  );
}

/* -------------------------------- FAQ -------------------------------- */

export const HOME_FAQ = [
  {
    q: "What amount has to be paid while placing the order?",
    a: "You pay one month’s rent in advance and a refundable security deposit. You can pay the entire amount online or use the NACH facility. Rent is due by the 7th of every month.",
  },
  {
    q: "What is the security deposit?",
    a: "A refundable security deposit of about one month’s rent is paid when you book. It is refunded after you end your subscription and we pick up the products.",
  },
  {
    q: "Do you charge for delivery and installation?",
    a: "No. Delivery and installation are free. If delivery is to a building without a lift, a labour fee based on the number of products and floor level is collected in cash at delivery.",
  },
  {
    q: "What documents are required?",
    a: "A photo ID (passport, PAN, driving licence or Aadhaar), an address proof (rental agreement, company letter, government proof or bank statement) and, if applicable, a landlord NOC.",
  },
  {
    q: "How can the contract be extended or terminated?",
    a: "The contract extends automatically. To close it, email a written request at least 2 weeks before the intended closure date. Early termination forfeits the security deposit.",
  },
  {
    q: "What if a product gets damaged?",
    a: "Minor damage is ignored. If a product is rendered unusable, you pay an amount based on its market value minus the total rent you have paid.",
  },
];

export function HomeFAQ() {
  return (
    <section className="v-section v-container" aria-labelledby="v-faq-title">
      <div className="v-faq">
        <div>
          <SectionHeader
            id="v-faq-title"
            eyebrow="FAQ"
            title="Questions, answered"
            description="Everything you need to know before you rent."
          />
          <Link to="/faq" className="v-link">
            Read all FAQs <Icon name="arrowRight" size={16} className="v-btn__arrow" />
          </Link>
        </div>
        <div className="v-faq__list">
          {HOME_FAQ.map((item) => (
            <details key={item.q} className="v-faq__item">
              <summary>
                {item.q}
                <Icon name="plus" size={18} className="v-faq__icon" />
              </summary>
              <p>{item.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------- Blog ------------------------------- */

const POSTS = [
  { to: "/blogs/buying-vs-renting", title: "Renting vs buying: which is better for you?" },
  { to: "/blogs/beginners-guide", title: "A beginner’s guide to renting furniture" },
  { to: "/blogs/tips-for-renting-furniture", title: "Tips for renting furniture" },
  { to: "/blogs/types-of-wood", title: "Types of wood used in furniture" },
];

export function BlogLinks() {
  return (
    <section className="v-section v-container" aria-labelledby="v-blog-title">
      <SectionHeader id="v-blog-title" eyebrow="From the blog" title="Guides for renting well" />
      <ul className="v-posts">
        {POSTS.map((post, index) => (
          <li key={post.to}>
            <Link to={post.to} className="v-post">
              <span className="v-post__num">{String(index + 1).padStart(2, "0")}</span>
              <span className="v-post__title">{post.title}</span>
              <span className="v-post__go">
                Read <Icon name="arrowRight" size={16} className="v-btn__arrow" />
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ----------------------------- Final CTA ----------------------------- */

export function FinalCTA() {
  return (
    <section className="v-section v-container" aria-labelledby="v-cta-title">
      <div className="v-cta">
        <div>
          <h2 id="v-cta-title" className="v-cta__title">Your next home, ready in days.</h2>
          <p className="v-cta__text">Pick what you need, choose a tenure, and we’ll deliver and set it up.</p>
        </div>
        <div className="v-cta__actions">
          <Button to={endpoint.getPath("furniture")} variant="accent" size="lg" arrow>
            Start renting
          </Button>
          <Button to={endpoint.getPath("package")} variant="ghost" size="lg" className="v-cta__ghost">
            Explore packages
          </Button>
        </div>
      </div>
    </section>
  );
}
